import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Landmark, Wallet, Plus, Pencil, Trash2, Ban, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const EMPTY_FORM = { name: '', type: 'bank', accountNumber: '', openingBalance: '0', notes: '' };

export default function BankAccounts() {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/bank-accounts`);
      setAccounts(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditing(null); setShowDialog(true); };
  const openEdit = (a) => {
    setForm({ name: a.name, type: a.type, accountNumber: a.accountNumber || '', openingBalance: String(a.openingBalance || 0), notes: a.notes || '' });
    setEditing(a.accountId);
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Enter an account name'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/accounting/bank-accounts/${editing}`, {
          name: form.name, accountNumber: form.accountNumber, openingBalance: parseFloat(form.openingBalance) || 0, notes: form.notes,
        });
        toast.success('Account updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/accounting/bank-accounts`, {
          name: form.name, type: form.type, accountNumber: form.accountNumber, openingBalance: parseFloat(form.openingBalance) || 0, notes: form.notes,
        });
        toast.success('Account created');
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await axios.put(`${BACKEND_URL}/api/accounting/bank-accounts/${a.accountId}`, { isActive: !a.isActive });
      toast.success(a.isActive ? 'Account deactivated' : 'Account reactivated');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update account');
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? Only possible if it has zero tagged transactions.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/accounting/bank-accounts/${a.accountId}`);
      toast.success('Account deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete account');
    }
  };

  const exportCsv = async (a) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/bank-accounts/${a.accountId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement_${a.name.replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Statement exported');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to export statement');
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="bank-accounts-page">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Landmark className="h-6 w-6 text-teal-600" /> Bank / Cash Accounts
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Reference balances only — running balance is always derived from tagged receipts and payments,
            never manually overridden. Tally stays the accounting source of truth.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="add-bank-account-btn">
            <Plus className="h-4 w-4" /> Add Account
          </Button>
        )}
      </header>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
      ) : accounts.length === 0 ? (
        <Card className="text-center py-12 text-slate-400 text-sm" data-testid="no-bank-accounts">
          No accounts yet. {isAdmin && 'Add one to start tagging receipts and payments.'}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bank-accounts-grid">
          {accounts.map((a) => (
            <Card key={a.accountId} className={`p-4 ${!a.isActive ? 'opacity-50' : ''}`} data-testid={`bank-account-card-${a.accountId}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {a.type === 'cash' ? <Wallet className="h-4 w-4 text-amber-600" /> : <Landmark className="h-4 w-4 text-teal-600" />}
                  <span className="font-semibold text-slate-900">{a.name}</span>
                  {!a.isActive && <span className="text-[10px] uppercase bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">Inactive</span>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => exportCsv(a)} data-testid={`export-account-${a.accountId}`} title="Export statement CSV" className="p-1 text-slate-400 hover:text-emerald-600"><Download className="h-3.5 w-3.5" /></button>
                  {isAdmin && (
                    <>
                      <button onClick={() => openEdit(a)} data-testid={`edit-account-${a.accountId}`} className="p-1 text-slate-400 hover:text-teal-600"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => toggleActive(a)} data-testid={`toggle-account-${a.accountId}`} className="p-1 text-slate-400 hover:text-amber-600"><Ban className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(a)} data-testid={`delete-account-${a.accountId}`} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                </div>
              </div>
              {a.accountNumber && <div className="text-xs text-slate-400 mt-1 font-mono">{a.accountNumber}</div>}
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-slate-400">Opening</div><div className="font-mono">{inr(a.openingBalance)}</div></div>
                <div><div className="text-slate-400">In / Out</div><div className="font-mono text-emerald-700">+{inr(a.inflow)}</div><div className="font-mono text-rose-700">-{inr(a.outflow)}</div></div>
                <div><div className="text-slate-400">Balance</div><div className="font-mono font-bold text-slate-900" data-testid={`account-balance-${a.accountId}`}>{inr(a.runningBalance)}</div></div>
              </div>
              <div className="text-[10px] text-slate-400 mt-2">{a.txnCount} tagged transaction{a.txnCount === 1 ? '' : 's'}</div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="bank-account-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Account' : 'Add Account'}</DialogTitle>
            <DialogDescription>Reference-only bank/cash account — balance is always derived from tagged transactions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="account-name-input" />
            </div>
            {!editing && (
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Account Number (optional)</Label>
              <Input value={form.accountNumber} onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))} className="mt-1" data-testid="account-number-input" />
            </div>
            <div>
              <Label className="text-xs">Opening Balance (₹)</Label>
              <Input type="number" value={form.openingBalance} onChange={(e) => setForm((f) => ({ ...f, openingBalance: e.target.value }))} className="mt-1" data-testid="account-opening-balance-input" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1" data-testid="account-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-bank-account-btn">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
